import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useProject } from "./useProject";

export interface ActiveCampaignStats {
  emailsSent: number;
  activeContacts: number;
  lastEmailSent: string | null;
}

export function useActiveCampaignStats() {
  const { currentProject } = useProject();
  const projectId = currentProject === '3m' ? 1 : 2;

  return useQuery({
    queryKey: ['active-campaign-stats', currentProject],
    queryFn: async (): Promise<ActiveCampaignStats> => {
      const thirtyDaysAgo = new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString();

      const [sentResult, lastEmailResult, activeContactsResult] = await Promise.all([
        // Total emails enviados
        supabase
          .from('email_tracking')
          .select('*', { count: 'exact', head: true })
          .eq('project_id', projectId)
          .eq('status', 'sent'),

        // Último email enviado
        supabase
          .from('email_tracking')
          .select('sent_at')
          .eq('project_id', projectId)
          .not('sent_at', 'is', null)
          .order('sent_at', { ascending: false })
          .limit(1),

        // Contactos activos (emails únicos últimos 30 días)
        supabase
          .from('email_tracking')
          .select('recipient_email')
          .eq('project_id', projectId)
          .gte('sent_at', thirtyDaysAgo)
      ]);

      const uniqueEmails = new Set(
        activeContactsResult.data?.map(r => r.recipient_email) || []
      );

      return {
        emailsSent: sentResult.count || 0,
        activeContacts: uniqueEmails.size,
        lastEmailSent: lastEmailResult.data?.[0]?.sent_at || null
      };
    },
    enabled: !!currentProject,
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000
  });
}

// Hook para enviar email de reporte
export function useSendReportEmail() {
  const { currentProject } = useProject();
  const projectId = currentProject === '3m' ? 1 : 2;

  const sendEmail = async ({
    submissionId,
    recipientEmail,
    recipientName,
    reportUrl,
    subject
  }: {
    submissionId: number;
    recipientEmail: string;
    recipientName: string;
    reportUrl: string;
    subject?: string;
  }) => {
    const { data, error } = await supabase.functions.invoke('active-campaign-integration', {
      body: {
        action: 'send-report-email',
        projectId,
        submissionId,
        recipientEmail,
        recipientName,
        reportUrl,
        subject
      }
    });

    if (error) {
      throw new Error(error.message);
    }

    return data;
  };

  return { sendEmail };
}
